// @spec AUD-STR-017
// @spec AUD-STR-018
// @spec AUD-STR-020
// @spec AUD-STR-042
// @spec AUD-STR-045
'use client';

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import {
  AuditConfigCard,
  ConfigureRetentionDialog,
  ConfigureSiemDialog,
  EditAuditConfigDialog,
  LegalHoldSection,
  RetentionStatusCard,
  SiemStatusCard,
  useAuditConfig,
  useRetentionStatus,
  useSiemConfig,
  useSiemStatus,
} from '@/features/settings/audit';
import i18n from '@/lib/i18n';
import { ApiClientError } from '@/types/api';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/settings/audit-settings')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.auditSettings'),
  }),
  component: AuditSettingsPage,
});

/**
 * Organization audit settings page for managing audit configuration,
 * retention policies, legal holds, and SIEM integrations.
 *
 * Sections:
 * - Audit Configuration: Configure event category tracking and sensitivity levels
 * - Retention Policy: Configure how long audit events are retained
 * - Legal Holds: Prevent deletion of audit events during investigations
 * - SIEM Integration: Forward audit events to external SIEM platforms
 */
function AuditSettingsPage() {
  const { t } = useTranslation('settings');
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [isRetentionDialogOpen, setIsRetentionDialogOpen] = useState(false);
  const [isSiemDialogOpen, setIsSiemDialogOpen] = useState(false);

  // Audit configuration
  const {
    data: config,
    isLoading: isLoadingConfig,
    error: configError,
    refetch: refetchConfig,
  } = useAuditConfig();

  // Retention status
  const {
    data: retentionStatus,
    isLoading: isLoadingRetention,
    error: retentionError,
    refetch: refetchRetention,
  } = useRetentionStatus();

  // SIEM config and status
  const {
    data: siemConfig,
    isLoading: isLoadingSiemConfig,
    error: siemConfigError,
  } = useSiemConfig();

  const {
    data: siemStatus,
    isLoading: isLoadingSiemStatus,
    refetch: refetchSiemStatus,
  } = useSiemStatus();

  // Check if SIEM is not configured (404 error or SIEM_NOT_CONFIGURED code)
  const siemNotConfigured =
    siemConfigError instanceof ApiClientError &&
    (siemConfigError.status === 404 ||
      siemConfigError.code === 'SIEM_NOT_CONFIGURED' ||
      siemConfigError.message.includes('SIEM_NOT_CONFIGURED'));

  // Check if audit config doesn't exist yet (404)
  const configNotFound = configError instanceof ApiClientError && configError.status === 404;

  return (
    <div className="p-8">
      <div className="mx-auto max-w-4xl space-y-8">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">{t('auditSettingsPage.title')}</h1>
          <p className="mt-1 text-muted-foreground">{t('auditSettingsPage.description')}</p>
        </header>

        {/* Audit Configuration Card */}
        <section>
          <h2 className="text-xl font-medium mb-4">{t('auditSettingsPage.auditConfiguration')}</h2>
          <AuditConfigCard
            config={config ?? null}
            isLoading={isLoadingConfig}
            error={configNotFound ? null : (configError ?? null)}
            onConfigureClick={() => {
              setConfigDialogOpen(true);
            }}
            onRetry={() => {
              void refetchConfig();
            }}
          />
        </section>

        {/* Retention Status Card */}
        <section>
          <h2 className="text-xl font-medium mb-4">{t('auditSettingsPage.retentionPolicy')}</h2>
          <RetentionStatusCard
            status={retentionStatus ?? null}
            isLoading={isLoadingRetention}
            error={retentionError ?? null}
            onConfigureClick={() => {
              setIsRetentionDialogOpen(true);
            }}
            onRetry={() => {
              void refetchRetention();
            }}
          />
        </section>

        {/* Legal Holds Section */}
        <section>
          <h2 className="text-xl font-medium mb-4">{t('auditSettingsPage.legalHolds')}</h2>
          <LegalHoldSection />
        </section>

        {/* SIEM Integration Card */}
        <section>
          <h2 className="text-xl font-medium mb-4">{t('auditSettingsPage.siemIntegration')}</h2>
          <SiemStatusCard
            config={siemConfig ?? null}
            status={siemStatus ?? null}
            isLoading={isLoadingSiemConfig || isLoadingSiemStatus}
            isNotConfigured={siemNotConfigured}
            error={siemNotConfigured ? null : (siemConfigError ?? null)}
            onConfigureClick={() => {
              setIsSiemDialogOpen(true);
            }}
            onRetry={() => {
              void refetchSiemStatus();
            }}
          />
        </section>

        {/* Dialogs */}
        <EditAuditConfigDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          onSuccess={() => {
            void refetchConfig();
          }}
        />

        <ConfigureRetentionDialog
          open={isRetentionDialogOpen}
          onOpenChange={setIsRetentionDialogOpen}
          onSuccess={() => {
            void refetchRetention();
          }}
        />

        <ConfigureSiemDialog
          open={isSiemDialogOpen}
          onOpenChange={setIsSiemDialogOpen}
          onSuccess={() => {
            void refetchSiemStatus();
          }}
        />
      </div>
    </div>
  );
}
